import React from 'react';
import { ArrowDown, Heart, BookOpen, ShieldCheck, Star } from 'lucide-react';
import { TestimonialCard } from './components/TestimonialCard';
import { PurchasePopup } from './components/PurchasePopup';
export function App() {
  const scrollToPurchase = () => {
    const element = document.getElementById('comprar');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen font-serif bg-gradient-to-b from-[#FDF4F5] via-[#F8E8EE] to-[#F2D7D9] text-gray-800 overflow-x-hidden" data-id="element-0">
      <PurchasePopup data-id="element-1" />

      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-2">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100/80 text-purple-800 text-sm font-medium mb-8 backdrop-blur-sm border border-purple-200" data-id="element-4">
            <Heart size={16} className="fill-purple-800" data-id="element-5" />
            <span data-id="element-6">Para la madre que lo da todo</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-purple-900 mb-6 leading-tight tracking-tight" data-id="element-7">
            No estás rota. <br data-id="element-8" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 italic" data-id="element-9">
              Estás cansada.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-10">
            Una guía honesta para madres agotadas que necesitan descanso, no más
            exigencia.
          </p>

          <div className="relative group cursor-pointer max-w-md mx-auto mb-12" onClick={scrollToPurchase} data-id="element-11">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-500" data-id="element-12"></div>
            <img src="https://cdn.magicpatterns.com/uploads/pmEvQGJeMYvyuTNeEQKqSu/foto_1.png" alt="Portada del libro No estás rota: estás cansada" className="relative rounded-xl shadow-2xl w-full transform group-hover:scale-[1.01] transition duration-500" data-id="element-13" />
          </div>

          <button onClick={scrollToPurchase} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg md:text-xl font-bold py-4 px-10 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto" data-id="element-14">
            Necesito leer esto
            <ArrowDown size={20} data-id="element-15" />
          </button>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none" data-id="element-16">
          <div className="absolute top-20 left-10 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-17"></div>
          <div className="absolute top-40 right-10 w-64 h-64 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-18"></div>
          <div className="absolute -bottom-8 left-1/2 w-64 h-64 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" data-id="element-19"></div>
        </div>
      </header>

      {/* Main Sales Copy */}
      <main className="max-w-3xl mx-auto px-6 pb-24" data-id="element-20">
        {/* Section 1: The Hook */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-21">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-22">
            Y nadie te lo dijo.
          </h2>
          <p data-id="element-23">
            Tal vez porque decirlo implicaría aceptar una verdad incómoda: que a
            las madres se les pide demasiado… y se les devuelve muy poco.
          </p>
          <p data-id="element-24">
            Si llegaste hasta aquí, es probable que seas una de esas mujeres
            que:
          </p>
          <ul className="list-none pl-0 space-y-3 my-6" data-id="element-25">
            {['Sostiene a todos', 'Resuelve todo', 'Cumple siempre', 'Funciona incluso cuando ya no puede más'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700" data-id="element-26">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-500" data-id="element-27"></div>
                {item}
              </li>)}
          </ul>
          <p className="font-medium text-purple-800 text-xl italic" data-id="element-28">
            Eres fuerte. Responsable. Comprometida.
          </p>
          <p className="font-bold text-2xl mt-2" data-id="element-29">
            Y estás profundamente cansada.
          </p>
          <p data-id="element-30">
            No cansada "normal". No cansada de un mal día. Cansada en el cuerpo,
            en la mente y en el alma.
          </p>
          <p data-id="element-31">
            Ese cansancio que no se va ni durmiendo. Ese que se queda aunque
            todo "esté bien". Ese que no sabes cómo explicar sin sentir culpa.
          </p>
          <p data-id="element-32">Y entonces aparece la pregunta más peligrosa de todas:</p>
          <p className="text-xl font-bold text-center my-8 p-6 bg-white/50 rounded-xl border border-purple-100" data-id="element-33">
            "¿Qué me pasa a mí?"
          </p>
        </section>

        <TestimonialCard name="María Laura" age={34} city="Buenos Aires" quote="Lloré leyendo las primeras páginas. Por fin alguien entiende lo que siento sin juzgarme." data-id="element-34" />

        {/* Section 2: The Guilt */}
        <section className="prose prose-lg prose-purple mx-auto mb-16 mt-16" data-id="element-35">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-36">
            Cuando el cansancio se vuelve culpa
          </h2>
          <p data-id="element-37">Te dijeron que es normal. Que es una etapa. Que ya pasará.</p>
          <p data-id="element-38">Pero pasan los meses… y el cansancio sigue ahí.</p>
          <p data-id="element-39">Entonces empiezas a exigirte más.</p>
          <p data-id="element-40">Más paciencia. Más gratitud. Más organización. Más fuerza.</p>
          <p data-id="element-41">
            Te repites que deberías poder. Que otras pueden. Que no es para
            tanto.
          </p>
          <p data-id="element-42">Y sin darte cuenta, empiezas a creer que el problema eres tú.</p>
          <div className="bg-purple-50 p-8 rounded-2xl border-l-4 border-purple-400 my-8" data-id="element-43">
            <p className="font-bold text-lg mb-2" data-id="element-44">Pero nadie te dice esto:</p>
            <p className="text-2xl font-serif italic text-purple-900" data-id="element-45">
              No necesitas arreglarte. Necesitas descansar.
            </p>
          </div>
        </section>

        {/* Section 3: The Invisible Load */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-46">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-47">
            El agotamiento que nadie ve
          </h2>
          <p data-id="element-48">
            Despiertas cansada. Pasas el día apagando incendios. Te acuestas
            sintiendo que no alcanzó.
          </p>
          <p data-id="element-49">Sonríes afuera. Lloras en silencio adentro.</p>
          <p data-id="element-50">Estás rodeada de gente… pero emocionalmente sola.</p>
          <p data-id="element-51">Porque nadie ve la mochila invisible que cargas.</p>
          <p data-id="element-52">
            Cada responsabilidad es una piedra. Cada expectativa, otra más. Cada
            "tú puedes", otra carga.
          </p>
          <p className="font-bold text-purple-800" data-id="element-53">
            No colapsas porque seas débil. Colapsas porque nadie puede vivir
            así.
          </p>
        </section>

        <TestimonialCard name="Carolina" age={38} city="Córdoba" quote="No sabía que necesitaba permiso para descansar hasta que lo leí. Cambió mi forma de verme." data-id="element-54" />

        {/* Section 4: The Battery Metaphor */}
        <section className="prose prose-lg prose-purple mx-auto mb-16 mt-16" data-id="element-55">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-56">
            No estás rota. Estás cansada.
          </h2>
          <p data-id="element-57">Imagina un teléfono al 1% de batería.</p>
          <p data-id="element-58">Va lento. Se apaga. No responde.</p>
          <p data-id="element-59">¿Dirías que está defectuoso? ¿O que necesita cargarse?</p>
          <p data-id="element-60">
            Sin embargo, a ti te exigen funcionar al 100% con la batería vacía.
          </p>
          <p data-id="element-61">Y encima te culpan por agotarte.</p>
          <p className="text-xl font-bold mt-6" data-id="element-62">
            Esto no es falta de capacidad. Es exceso de carga.
          </p>
          <p data-id="element-63">
            No necesitas otro libro que te diga cómo mejorar. No necesitas otra
            herramienta. No necesitas ser más fuerte.
          </p>
          <p data-id="element-64">Ya lo has sido demasiado tiempo.</p>
          <p data-id="element-65">Lo que necesitas es algo que casi nadie te da:</p>
          <p className="text-2xl text-center font-serif italic text-purple-800 my-6" data-id="element-66">
            Permiso para parar.
          </p>
        </section>

        <TestimonialCard name="Luciana" age={32} city="Rosario" quote="Pensé que era la única que se sentía así. Este libro me devolvió la esperanza." data-id="element-67" />

        {/* Section 5: The Solution */}
        <section className="prose prose-lg prose-purple mx-auto mb-16 mt-16" data-id="element-68">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-69">
            Esto es diferente
          </h2>
          <p data-id="element-70">
            <strong data-id="element-71">"No estás rota: estás cansada"</strong> no es un libro de
            autoayuda.
          </p>
          <p data-id="element-72">
            No viene a exigirte. No viene a corregirte. No viene a empujarte.
          </p>
          <p className="text-xl font-medium text-purple-800" data-id="element-73">
            Viene a sostenerte.
          </p>
          <p data-id="element-74">
            Es una guía honesta, directa y profundamente humana para madres
            agotadas que necesitan descanso, no más exigencia.
          </p>
          <p data-id="element-75">Un espacio seguro donde alguien, por fin, te dice:</p>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-100 text-center my-6" data-id="element-76">
            <p className="text-2xl font-serif italic text-gray-800" data-id="element-77">
              "Tiene sentido que estés así."
            </p>
          </div>
        </section>

        {/* Section 6: Why nothing worked */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-78">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-79">
            ¿Por qué nada de lo anterior funcionó?
          </h2>
          <p data-id="element-80">Tal vez probaste:</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 my-6 pl-0 list-none" data-id="element-81">
            {['Libros de crianza', 'Terapia enfocada en "mejorar"', 'Frases motivacionales', 'Rutinas y organización', 'Pensar positivo', 'Agradecer más'].map((item, i) => <li key={i} className="bg-white/60 p-3 rounded-lg flex items-center gap-2 text-sm md:text-base" data-id="element-82">
                <X className="text-red-400 w-4 h-4" data-id="element-83" /> {item}
              </li>)}
          </ul>
          <p data-id="element-84">Y aun así… el cansancio no se fue.</p>
          <p data-id="element-85">Porque todas esas soluciones parten de la misma idea:</p>
          <p className="italic" data-id="element-86">"Tienes que hacer algo más para estar bien."</p>
          <p data-id="element-87">Más esfuerzo. Más trabajo interno. Más carga.</p>
          <p className="font-bold text-purple-900" data-id="element-88">
            Cuando el problema no es que no hagas suficiente. Es que haces
            demasiado.
          </p>
        </section>

        <TestimonialCard name="Gabriela" age={41} city="Mendoza" quote="Simple, directo y profundamente sanador. Lo releo cada vez que me exijo demasiado." data-id="element-89" />

        {/* Section 7: The Truth */}
        <section className="prose prose-lg prose-purple mx-auto mb-16 mt-16" data-id="element-90">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-91">
            La verdad que nadie nombra
          </h2>
          <p data-id="element-92">El agotamiento materno no es un problema personal.</p>
          <p data-id="element-93">Es emocional. Es sistémico. Es invisible.</p>
          <p className="font-bold text-xl" data-id="element-94">
            No estás fallando. Estás sobrecargada.
          </p>
          <p data-id="element-95">Y nadie te enseñó a soltar sin culpa.</p>
          <p data-id="element-96">Por eso esta guía no te pide acción. Te pide pausa.</p>
          <p data-id="element-97">No te enseña a hacer más. Te enseña a soltar.</p>
        </section>

        {/* Section 8: Real Rest */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-98">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-99">
            ¿Qué pasa cuando una madre descansa de verdad?
          </h2>
          <p data-id="element-100">
            No cuando duerme un poco más. Sino cuando descansa emocionalmente.
          </p>
          <ul className="space-y-2 my-6 list-none pl-0" data-id="element-101">
            {['Respira mejor', 'Se culpa menos', 'Se exige menos', 'Se siente más humana', 'Recupera pedazos de sí misma'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-102">
                <CheckCircle2 className="text-green-500 w-5 h-5" data-id="element-103" />
                <span data-id="element-104">{item}</span>
              </li>)}
          </ul>
          <p data-id="element-105">No porque cambió quién es. Sino porque dejó de pelear consigo.</p>
        </section>

        {/* Section 9: Is this for you? */}
        <section className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-purple-100 mb-16" data-id="element-106">
          <h2 className="text-3xl font-bold text-purple-900 mb-8 text-center" data-id="element-107">
            Este libro es para ti si…
          </h2>
          <ul className="space-y-4" data-id="element-108">
            {['Estás cansada y no sabes explicar por qué', 'Te sientes culpable por querer parar', 'Sientes que perdiste a la mujer que eras', 'Estás agotada de exigirte', 'Necesitas que alguien te entienda de verdad'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-109">
                <div className="bg-purple-100 p-1 rounded-full mt-1" data-id="element-110">
                  <CheckCircle2 className="text-purple-600 w-5 h-5" data-id="element-111" />
                </div>
                <span className="text-lg text-gray-800" data-id="element-112">{item}</span>
              </li>)}
          </ul>
          <div className="mt-8 text-center" data-id="element-113">
            <p className="text-gray-600 mb-2" data-id="element-114">
              No para juzgarte. No para corregirte.
            </p>
            <p className="text-xl font-bold text-purple-800" data-id="element-115">
              Para decirte: No estás fallando. Estás cansada.
            </p>
          </div>
        </section>

        <TestimonialCard name="Valeria" age={36} city="Mar del Plata" quote="Finalmente entendí: no estoy fallando, estoy cansada. Eso lo cambió todo." data-id="element-116" />

        {/* Section 10: Origin Story */}
        <section className="prose prose-lg prose-purple mx-auto mb-16 mt-16" data-id="element-117">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-118">
            El origen de esta guía
          </h2>
          <p data-id="element-119">Este libro nació de una frase dicha en voz baja:</p>
          <p className="italic text-lg" data-id="element-120">"No puedo más… pero igual sigo."</p>
          <p data-id="element-121">
            Nació cuando alguien entendió que el problema no era ella, sino el
            peso que cargaba en silencio.
          </p>
          <p data-id="element-122">
            Nació para las madres que siguen de pie mientras por dentro se están
            cayendo.
          </p>

          <div className="my-10 p-8 bg-purple-900 text-white rounded-2xl text-center" data-id="element-123">
            <p className="text-2xl font-bold mb-4" data-id="element-124">Esto no es un libro</p>
            <p className="text-3xl font-serif italic mb-6" data-id="element-125">Es un permiso</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-lg mx-auto" data-id="element-126">
              <div className="flex items-center gap-2" data-id="element-127">
                <Star size={16} className="text-yellow-400" data-id="element-128" /> Descansar sin
                culpa
              </div>
              <div className="flex items-center gap-2" data-id="element-129">
                <Star size={16} className="text-yellow-400" data-id="element-130" /> Soltar la
                autoexigencia
              </div>
              <div className="flex items-center gap-2" data-id="element-131">
                <Star size={16} className="text-yellow-400" data-id="element-132" /> Dejar de
                preguntarte qué está mal
              </div>
              <div className="flex items-center gap-2" data-id="element-133">
                <Star size={16} className="text-yellow-400" data-id="element-134" /> Recordar que
                sigues siendo mujer
              </div>
            </div>
            <p className="text-2xl font-bold mt-8" data-id="element-135">Sentir alivio</p>
          </div>

          <p data-id="element-136">Si algo dentro de ti se movió leyendo esto…</p>
          <p data-id="element-137">
            Si sentiste un nudo en la garganta. Si pensaste "esto soy yo". Si
            por primera vez alguien puso palabras a lo que sientes…
          </p>
          <p className="font-bold text-xl" data-id="element-138">Entonces esta guía es para ti.</p>
          <p data-id="element-139">No para cambiarte. Para sostenerte.</p>
        </section>

        {/* Bonuses Section */}
        <section className="mb-20" data-id="element-140">
          <div className="text-center mb-10" data-id="element-141">
            <span className="bg-pink-100 text-pink-800 px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide" data-id="element-142">
              Regalo especial
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mt-4 mb-2" data-id="element-143">
              Bonos GRATIS por hoy
            </h2>
            <p className="text-gray-600" data-id="element-144">
              Complementos perfectos para tu descanso
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-145">
            <div className="group cursor-pointer" onClick={scrollToPurchase} data-id="element-146">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-147">
                <img src="https://cdn.magicpatterns.com/uploads/omSMZXhdwk8zzNhAY5yrwK/foto_2.png" alt="Bono 1" className="w-full h-auto transform group-hover:scale-105 transition duration-500" data-id="element-148" />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md" data-id="element-149">
                  GRATIS
                </div>
              </div>
            </div>
            <div className="group cursor-pointer" onClick={scrollToPurchase} data-id="element-150">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-151">
                <img src="https://cdn.magicpatterns.com/uploads/kbmNRjmrhR4KYa9CuqCJMk/foto_3.png" alt="Bono 2" className="w-full h-auto transform group-hover:scale-105 transition duration-500" data-id="element-152" />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md" data-id="element-153">
                  GRATIS
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-6 italic" data-id="element-154">
            * Estos bonos son completamente gratis al adquirir la guía el día de
            hoy
          </p>
        </section>

        {/* Purchase Section */}
        <section id="comprar" className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-purple-100 relative" data-id="element-155">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400" data-id="element-156"></div>

          <div className="p-8 md:p-12 text-center" data-id="element-157">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-158">
              💛 Permítete esto
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-159">
              "No estás rota: estás cansada"
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-10" data-id="element-160">
              <img src="https://cdn.magicpatterns.com/uploads/pmEvQGJeMYvyuTNeEQKqSu/foto_1.png" alt="Portada libro" className="w-48 rounded-lg shadow-md rotate-[-5deg]" data-id="element-161" />
              <div className="text-left" data-id="element-162">
                <ul className="space-y-3 mb-6" data-id="element-163">
                  <li className="flex items-center gap-2 text-gray-700" data-id="element-164">
                    <CheckCircle2 size={18} className="text-green-500" data-id="element-165" /> Guía
                    digital completa
                  </li>
                  <li className="flex items-center gap-2 text-gray-700" data-id="element-166">
                    <CheckCircle2 size={18} className="text-green-500" data-id="element-167" /> Bono:
                    Cómo descansar sin culpa
                  </li>
                  <li className="flex items-center gap-2 text-gray-700" data-id="element-168">
                    <CheckCircle2 size={18} className="text-green-500" data-id="element-169" /> Bono:
                    Carta para la madre
                  </li>
                  <li className="flex items-center gap-2 text-gray-700" data-id="element-170">
                    <CheckCircle2 size={18} className="text-green-500" data-id="element-171" /> Acceso
                    inmediato
                  </li>
                </ul>
                <div className="flex items-end gap-2 mb-2" data-id="element-172">
                  <span className="text-5xl font-bold text-purple-900" data-id="element-173">
                    $19.990
                  </span>
                  <span className="text-gray-400 line-through mb-2 text-lg" data-id="element-174">
                    $39.990
                  </span>
                </div>
              </div>
            </div>

            <a href="link de tu checkout" className="block w-full max-w-md mx-auto bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xl md:text-2xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 animate-pulse-slow" data-id="element-175">
              Obtener mi guía ahora
            </a>

            <div className="mt-6 flex flex-col items-center gap-2 text-sm text-gray-500" data-id="element-176">
              <div className="flex items-center gap-2" data-id="element-177">
                <ShieldCheck size={16} data-id="element-178" />
                <span data-id="element-179">100% digital - descarga instantánea</span>
              </div>
              <p data-id="element-180">Acceso inmediato después de la compra</p>
            </div>
          </div>

          <div className="bg-gray-50 p-6 text-center border-t border-gray-100" data-id="element-181">
            <p className="text-gray-600 italic mb-4" data-id="element-182">
              "Permítete leerla. Permítete parar. Permítete descansar. Porque no
              estás rota. Estás cansada."
            </p>
            <div className="flex justify-center gap-1" data-id="element-183">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} size={16} className="text-yellow-400 fill-current" data-id="element-184" />)}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-purple-900 text-purple-200 py-12 text-center px-4" data-id="element-185">
        <p className="mb-4 font-serif italic text-xl" data-id="element-186">
          No estás rota. Estás cansada.
        </p>
        <p className="text-sm opacity-60" data-id="element-187">
          © {new Date().getFullYear()} Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}
// Helper component for list items
function CheckCircle2({
  className,
  size
}: {
  className?: string;
  size?: number;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-188">
      <circle cx="12" cy="12" r="10" data-id="element-189" />
      <path d="m9 12 2 2 4-4" data-id="element-190" />
    </svg>;
}
// Helper for X icon
function X({
  className,
  size
}: {
  className?: string;
  size?: number;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-191">
      <path d="M18 6 6 18" data-id="element-192" />
      <path d="m6 6 18 18" data-id="element-193" />
    </svg>;
}